import { useEffect } from 'react'

import { makeVar } from '@apollo/client'

import { ArrayOrValue } from '@web/types'

interface SequentialItem {
  order: number
  type: 'afterPrev' | 'withPrev'
  delaySeconds: number
  durationSeconds: number
  onExecute: () => void
  id: string
}

const sequentialListVar = makeVar<SequentialItem[]>([])
const playingVar = makeVar<boolean>(false)

export type RegistSequentialListInput = Pick<SequentialItem, 'onExecute' | 'order' | 'durationSeconds' | 'id'> &
  Partial<SequentialItem>

const fillDefaultValue = ({
  id,
  delaySeconds = 0,
  durationSeconds,
  onExecute,
  order,
  type = 'afterPrev',
}: RegistSequentialListInput): SequentialItem => {
  return {
    id,
    delaySeconds,
    durationSeconds,
    onExecute,
    order,
    type,
  }
}

export const useRegistToSequentialList = (
  input: ArrayOrValue<RegistSequentialListInput>,
  options?: { skip?: boolean },
) => {
  useEffect(() => {
    if (options?.skip) return () => null

    const newSequentialList = (Array.isArray(input) ? input : [input])
      .map(fillDefaultValue)
      .filter(({ id }) => !sequentialListVar().some(sequentialItem => sequentialItem.id === id))

    sequentialListVar(sequentialListVar().concat(newSequentialList))

    return () => {
      sequentialListVar(
        sequentialListVar().filter(sequentialItem => !newSequentialList.some(inserted => sequentialItem === inserted)),
      )
    }
  }, [input])
}

type PromiseSequence = () => Promise<unknown>

const executeSequenceByPromise = (sequentialItems: SequentialItem[], debug = true) => {
  const promiseGroups: PromiseSequence[][] = []

  const start = Date.now()

  playingVar(true)

  sequentialItems.reduce((prevPromiseGroup, sequentialItem) => {
    const promise = () =>
      new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!sequentialItems.length || playingVar() !== true) {
            reject(new Error(`[executeSequenceByPromise] No Items`))
            return
          }

          if (debug) {
            const after = Date.now() - start
            // eslint-disable-next-line no-console
            console.info(`after ${after}ms [${sequentialItem.id}] excuted`)
          }

          sequentialItem.onExecute()
          setTimeout(resolve, sequentialItem.durationSeconds * 1000)
        }, sequentialItem.delaySeconds * 1000)
      })

    if (!prevPromiseGroup || sequentialItem.type === 'afterPrev') {
      const newPromiseGroup = [promise]
      promiseGroups.push(newPromiseGroup)
      return newPromiseGroup
    }

    prevPromiseGroup.push(promise)
    return prevPromiseGroup
  }, null as PromiseSequence[] | null)

  promiseGroups
    .reduce((prevGroup, currentGroup) => {
      if (!prevGroup) return Promise.all(currentGroup.map(promise => promise()))
      return prevGroup.then(() => Promise.all(currentGroup.map(promise => promise())))
    }, null as Promise<unknown[]> | null)
    ?.catch(() => {
      if (debug) {
        const after = Date.now() - start
        // eslint-disable-next-line no-console
        console.info(`after ${after}ms sequential canceled`)
      }
    })
}
export const startSequence = (debug = false) => {
  const sortedSequentialList = sequentialListVar()
    .slice()
    .sort((a, b) => a.order - b.order)

  executeSequenceByPromise(sortedSequentialList, debug)
}

export const startSequenceByInput = (sequentialItemInputs: RegistSequentialListInput[], debug = false) => {
  const sequentialItems = sequentialItemInputs.map(fillDefaultValue)
  executeSequenceByPromise(sequentialItems, debug)
}

export const cleanUpSequentialList = () => {
  playingVar(false)
  sequentialListVar([])
}
